import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Location-aware axios interceptor.
 *
 * `LocationContext` calls `setActiveLocationId(id)` whenever the merchant
 * switches locations. Every outgoing request then automatically carries a
 * `location_id` query param unless it's already set by the caller. Sending
 * `null` (== "All Locations") skips the param so backend returns combined data.
 */
let _activeLocationId = null;
export const setActiveLocationId = (id) => { _activeLocationId = id || null; };
export const getActiveLocationId = () => _activeLocationId;

const createApiClient = (token) => {
  const client = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  client.interceptors.request.use((config) => {
    // Only tag endpoints that operate on tenant-scoped data. Auth, admin,
    // onboarding, and location-management endpoints must never carry it.
    const url = config.url || '';
    const skip = /^\/?(auth|admin|locations|untill\/instance|onboarding|settings)/.test(url);
    if (!_activeLocationId || skip) return config;
    config.params = { ...(config.params || {}) };
    if (config.params.location_id === undefined) {
      config.params.location_id = _activeLocationId;
    }
    return config;
  });
  return client;
};

export const api = {
  // Dashboard
  getDashboardStats: (token) => createApiClient(token).get('/dashboard/stats'),
  getRecentActivity: (token, limit = 10) => createApiClient(token).get(`/dashboard/recent-activity?limit=${limit}`),

  // Sync Logs
  getLogs: (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.sync_type) queryParams.append('sync_type', params.sync_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    return createApiClient(token).get(`/logs?${queryParams.toString()}`);
  },

  // Products
  getProducts: (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.sync_status) queryParams.append('sync_status', params.sync_status);
    return createApiClient(token).get(`/products?${queryParams.toString()}`);
  },
  syncProducts: (token) => createApiClient(token).post('/products/sync'),

  // Customers
  getCustomers: (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.sync_status) queryParams.append('sync_status', params.sync_status);
    return createApiClient(token).get(`/customers?${queryParams.toString()}`);
  },
  syncCustomers: (token) => createApiClient(token).post('/customers/sync'),

  // Payment Methods
  getPaymentMethods: (token) => createApiClient(token).get('/payments/methods'),
  updatePaymentMethod: (token, methodId, data) => 
    createApiClient(token).put(`/payments/methods/${methodId}?odoo_journal_id=${data.odoo_journal_id}&odoo_journal_name=${data.odoo_journal_name}`),

  // Sales
  getSales: (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.sync_status) queryParams.append('sync_status', params.sync_status);
    if (params.limit) queryParams.append('limit', params.limit);
    return createApiClient(token).get(`/sales?${queryParams.toString()}`);
  },
  syncSales: (token) => createApiClient(token).post('/sales/sync'),
  syncSalesFromUntill: (token, params = {}) =>
    createApiClient(token).post('/untill/sync-sales', null, { params }),

  // Inventory
  getInventory: (token, mismatchOnly = false) => 
    createApiClient(token).get(`/inventory?mismatch_only=${mismatchOnly}`),
  syncInventory: (token) => createApiClient(token).post('/inventory/sync'),

  // Accounting
  getJournalEntries: (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    return createApiClient(token).get(`/accounting/entries?${queryParams.toString()}`);
  },
  generateAccountingEntries: (token) => createApiClient(token).post('/accounting/generate'),

  // Settings
  getSettings: (token) => createApiClient(token).get('/settings'),
  updateSettings: (token, settings) => createApiClient(token).put('/settings', settings),

  // Manual Sync Trigger
  triggerSync: (token, syncType) => createApiClient(token).post(`/sync/trigger/${syncType}`),

  // Seed Data
  seedData: (token) => createApiClient(token).post('/seed-data'),

  // QuickBooks Online
  getQboAppCredentials: (token) => createApiClient(token).get('/qbo/app-credentials'),
  saveQboAppCredentials: (token, data) => createApiClient(token).post('/qbo/app-credentials', data),
  getQboStatus: (token) => createApiClient(token).get('/qbo/status'),
  getQboAuthorizeUrl: (token) => createApiClient(token).get('/qbo/authorize-url'),
  testQboConnection: (token) => createApiClient(token).post('/qbo/test'),
  disconnectQbo: (token) => createApiClient(token).delete('/qbo/connection'),

  // QuickBooks Sales Posting
  qboSalesSetup: (token) => createApiClient(token).post('/qbo/sales/setup'),
  getQboSalesConfig: (token) => createApiClient(token).get('/qbo/sales/config'),
  getQboSalesSummary: (token) => createApiClient(token).get('/qbo/sales/summary'),
  qboSalesSyncNow: (token) => createApiClient(token).post('/qbo/sales/sync-now'),
  qboPostTestSale: (token, params = {}) =>
    createApiClient(token).post('/qbo/sales/post-test-sale', null, { params }),
  qboSalesToggleRealtime: (token, enabled) =>
    createApiClient(token).post('/qbo/sales/realtime', { enabled }),
  getQboDepositAccounts: (token) => createApiClient(token).get('/qbo/sales/deposit-accounts'),
  getQboPaymentMappings: (token) => createApiClient(token).get('/qbo/sales/payment-mappings'),
  saveQboPaymentMappings: (token, mappings) =>
    createApiClient(token).put('/qbo/sales/payment-mappings', { mappings }),

  // Odoo ERP
  getOdooCredentials: (token) => createApiClient(token).get('/odoo/credentials'),
  saveOdooCredentials: (token, data) => createApiClient(token).post('/odoo/credentials', data),
  getOdooStatus: (token) => createApiClient(token).get('/odoo/status'),
  testOdooConnection: (token) => createApiClient(token).post('/odoo/test'),
  disconnectOdoo: (token) => createApiClient(token).delete('/odoo/credentials'),

  // UnTill Instance (FR-2)
  getUnTillInstance: (token) => createApiClient(token).get('/untill/instance'),
  saveUnTillInstance: (token, data) => createApiClient(token).post('/untill/instance', data),
  testUnTillInstance: (token) => createApiClient(token).post('/untill/instance/test'),
  deleteUnTillInstance: (token) => createApiClient(token).delete('/untill/instance'),

  // Locations (multi-instance per tenant)
  listLocations: (token, include_archived = false) =>
    createApiClient(token).get('/locations', { params: { include_archived } }),
  compareLocations: (token, params = {}) => createApiClient(token).get('/locations/compare', { params }),
  compareLocationsExportUrl: (token, params = {}) => {
    // Build a signed URL suitable for direct `<a href>` download. Passes the
    // access_token as a header via fetch() when downloading.
    const qs = new URLSearchParams(params).toString();
    return `${API_URL}/api/locations/compare/export${qs ? `?${qs}` : ''}`;
  },
  createLocation: (token, data) => createApiClient(token).post('/locations', data),
  updateLocation: (token, id, data) => createApiClient(token).put(`/locations/${id}`, data),
  testLocation: (token, id) => createApiClient(token).post(`/locations/${id}/test`),
  setDefaultLocation: (token, id) => createApiClient(token).post(`/locations/${id}/set-default`),
  deleteLocation: (token, id, mode = 'archive') =>
    createApiClient(token).delete(`/locations/${id}`, { params: { mode } }),

  // Onboarding + Go-Live
  getOnboardingStatus: (token) => createApiClient(token).get('/onboarding/status'),
  setGoLive: (token, enabled) => createApiClient(token).post('/onboarding/go-live', { enabled }),

  // Account environment (sandbox / live)
  resendVerification: (token) => createApiClient(token).post('/auth/resend-verification'),
  getAccountMode: (token) => createApiClient(token).get('/account/mode'),
  goLive: (token, confirm) => createApiClient(token).post('/account/go-live', { confirm }),
  backToSandbox: (token) => createApiClient(token).post('/account/sandbox'),

  // Super Admin
  adminListTenants: (token) => createApiClient(token).get('/admin/tenants'),
  adminDeleteTenant: (token, user_id) => createApiClient(token).delete(`/admin/tenants/${user_id}`),
  adminPromote: (token, user_id) => createApiClient(token).post(`/admin/promote/${user_id}`),
  adminCreateUser: (token, data) => createApiClient(token).post('/admin/users', data),
  adminImpersonate: (token, user_id) => createApiClient(token).post(`/admin/impersonate/${user_id}`),
  adminSendReset: (token, user_id) => createApiClient(token).post(`/admin/users/${user_id}/send-reset`),

  // Test-Sync Dry Run (FR-7)
  onboardingDryRun: (token, sample_size = 5, post_odoo_drafts = false) =>
    createApiClient(token).post('/onboarding/dry-run', { sample_size, post_odoo_drafts }),

  // Password change (force rotation for admin-created users)
  changePassword: (token, current_password, new_password) =>
    createApiClient(token).post('/auth/change-password', { current_password, new_password }),

  // Agents
  askLogAssistant: (token, question, session_id) =>
    createApiClient(token).post('/agents/logs/ask', { question, session_id }),
  triageAlerts: (token) => createApiClient(token).post('/agents/alerts/triage'),
  listAgentActions: (token) => createApiClient(token).get('/agents/alerts/actions'),
  confirmAgentAction: (token, action_id) =>
    createApiClient(token).post('/agents/alerts/actions/confirm', { action_id }),
  undoAgentAction: (token, action_id) =>
    createApiClient(token).post('/agents/alerts/actions/undo', { action_id }),
  proposeMappings: (token, target_system = 'quickbooks', limit = 15) =>
    createApiClient(token).post('/agents/mapping/propose', { target_system, limit }),
  confirmMapping: (token, action_id) =>
    createApiClient(token).post('/agents/mapping/confirm', { action_id }),
  clearDemoData: (token) => createApiClient(token).post('/clear-demo-data'),
  seedDemoData: (token) => createApiClient(token).post('/seed-data'),

  getAgentSettings: (token) => createApiClient(token).get('/settings/agents'),
  saveAgentSettings: (token, data) => createApiClient(token).put('/settings/agents', data),

  // Destinations (per-location QBO/Odoo profile bindings)
  listDestinations: (token) => createApiClient(token).get('/destinations'),
  createQboProfile: (token, profile_name) =>
    createApiClient(token).post('/destinations/qbo/profile', { profile_name }),
  createOdooProfile: (token, profile_name) =>
    createApiClient(token).post('/destinations/odoo/profile', { profile_name }),
  deleteQboProfile: (token, profile_id) =>
    createApiClient(token).delete(`/destinations/qbo/${profile_id}`),
  deleteOdooProfile: (token, profile_id) =>
    createApiClient(token).delete(`/destinations/odoo/${profile_id}`),
  getLocationBinding: (token, location_id) =>
    createApiClient(token).get(`/locations/${location_id}/bindings`),
  saveLocationBinding: (token, location_id, data) =>
    createApiClient(token).put(`/locations/${location_id}/bindings`, data),

  getLocationWebhook: (token, location_id) =>
    createApiClient(token).get(`/locations/${location_id}/webhook`),
  rotateLocationWebhook: (token, location_id) =>
    createApiClient(token).post(`/locations/${location_id}/webhook/rotate`),

  // Per-profile QBO onboarding (existing endpoints support ?profile_id=…)
  qboAuthorizeUrl: (token, profile_id) =>
    createApiClient(token).get('/qbo/authorize-url', { params: { profile_id } }),
  qboSaveApp: (token, data, profile_id) =>
    createApiClient(token).post('/qbo/app-credentials', data, { params: { profile_id } }),
  odooSaveCreds: (token, data, profile_id) =>
    createApiClient(token).post('/odoo/credentials', data, { params: { profile_id } }),
  odooTest: (token, profile_id) =>
    createApiClient(token).post('/odoo/test', null, { params: { profile_id } }),
};

export default api;
